import React, { useState } from "react";
import { AlertTriangle, Check, X, Pencil } from "lucide-react";
import { api } from "@/lib/api";
import { DISCIPLINE_LABEL } from "@/lib/utils";

/**
 * NeedsReviewBadge — surfaced on sheets whose real identity (sheet number /
 * sheet title / discipline / revision) couldn't be extracted from the PDF
 * title block with high or medium confidence.
 *
 * Operational philosophy: we NEVER present low-confidence extraction as
 * authoritative. The badge is a quiet amber pill — admins can tap it to
 * inline-edit and override the values; non-admins see it as a read-only
 * caution signal.
 *
 * Props:
 *   - sheet: the full sheet object
 *   - canEdit: boolean — only admins / PMs / owners get the edit affordance
 *   - onSaved: optional callback after a successful save
 *   - size: "xs" (in plan-wall card) | "sm" (top bar, default)
 */
export default function NeedsReviewBadge({
  sheet,
  canEdit = false,
  onSaved = () => {},
  size = "sm",
}) {
  const [open, setOpen] = useState(false);
  const small = size === "xs";

  const pillCls = small
    ? "inline-flex items-center gap-1 px-1.5 py-px rounded text-[9px] font-mono uppercase tracking-wider border border-amber-500/40 bg-amber-500/10 text-amber-300"
    : "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-mono uppercase tracking-wider border border-amber-500/45 bg-amber-500/10 text-amber-300 hover:border-amber-400 transition-colors";

  return (
    <>
      <button
        type="button"
        onClick={canEdit ? () => setOpen(true) : undefined}
        className={`${pillCls} ${canEdit ? "cursor-pointer" : "cursor-default"}`}
        title={canEdit ? "Override sheet identity" : "This sheet number/title needs review"}
        data-testid={`needs-review-badge${small ? "-xs" : ""}`}
        aria-label="Needs review"
      >
        <AlertTriangle size={small ? 9 : 11} />
        <span>{small ? "REVIEW" : "Needs Review"}</span>
        {canEdit && !small && <Pencil size={10} className="opacity-60" />}
      </button>

      {open && canEdit && (
        <OverrideModal
          sheet={sheet}
          onClose={() => setOpen(false)}
          onSaved={(updated) => { onSaved(updated); setOpen(false); }}
        />
      )}
    </>
  );
}


function OverrideModal({ sheet, onClose, onSaved }) {
  const [form, setForm] = useState({
    sheet_number: sheet.sheet_number || "",
    sheet_title: sheet.sheet_title || "",
    discipline: sheet.discipline || "general",
    revision: sheet.revision || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const onSave = async () => {
    setError(null);
    const payload = {};
    if (form.sheet_number && form.sheet_number !== sheet.sheet_number) payload.sheet_number = form.sheet_number.trim();
    if (form.sheet_title && form.sheet_title !== sheet.sheet_title) payload.sheet_title = form.sheet_title.trim();
    if (form.discipline && form.discipline !== sheet.discipline) payload.discipline = form.discipline;
    if (form.revision && form.revision !== sheet.revision) payload.revision = form.revision.trim();
    if (Object.keys(payload).length === 0) {
      // Nothing to save — still clear needs_review by sending the current
      // sheet_number through (PATCH will mark source=manual + clear flag).
      payload.sheet_number = form.sheet_number.trim() || sheet.sheet_number;
    }
    try {
      setSaving(true);
      const r = await api.patch(`/sheets/${sheet.id}`, payload);
      onSaved(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      data-testid="needs-review-modal-backdrop"
    >
      <div
        className="bg-[var(--fop-surface)] border border-[var(--fop-border)] rounded-md p-5 w-full max-w-md mx-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="needs-review-modal"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="font-mono text-[11px] uppercase tracking-wider text-[var(--fop-text-2)]">
            Override Sheet Identity
          </div>
          <button
            onClick={onClose}
            className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)] transition-colors"
            aria-label="Close"
            data-testid="needs-review-modal-close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="text-[11px] font-mono uppercase tracking-wider text-amber-400/80 mb-4">
          Extraction confidence:{" "}
          <span className="text-amber-300">{sheet.extraction_confidence || "unknown"}</span>
          {sheet.extraction_source && (
            <> · source: <span className="text-amber-300">{sheet.extraction_source}</span></>
          )}
        </div>

        <div className="space-y-3">
          <Field
            label="Sheet number"
            value={form.sheet_number}
            onChange={(v) => setForm((f) => ({ ...f, sheet_number: v.toUpperCase() }))}
            placeholder="C-101"
            testid="needs-review-sheet-number"
            mono
            fieldConfidence={sheet.field_confidence?.sheet_number}
            ocrOriginal={sheet.ocr_original?.sheet_number}
          />
          <Field
            label="Sheet title"
            value={form.sheet_title}
            onChange={(v) => setForm((f) => ({ ...f, sheet_title: v }))}
            placeholder="Grading & Drainage Plan"
            testid="needs-review-sheet-title"
            fieldConfidence={sheet.field_confidence?.sheet_title}
            ocrOriginal={sheet.ocr_original?.sheet_title}
          />
          <div className="grid grid-cols-2 gap-3">
            <DisciplineField
              value={form.discipline}
              onChange={(v) => setForm((f) => ({ ...f, discipline: v }))}
              testid="needs-review-discipline"
              fieldConfidence={sheet.field_confidence?.discipline}
            />
            <Field
              label="Revision"
              value={form.revision}
              onChange={(v) => setForm((f) => ({ ...f, revision: v }))}
              placeholder="0"
              testid="needs-review-revision"
              mono
              fieldConfidence={sheet.field_confidence?.revision}
              ocrOriginal={sheet.ocr_original?.revision}
            />
          </div>
        </div>

        {error && (
          <div className="mt-3 text-[11px] text-red-400 font-mono" data-testid="needs-review-error">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-5">
          <button
            onClick={onClose}
            className="btn btn-ghost text-xs px-3"
            data-testid="needs-review-modal-cancel"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            className="btn btn-primary text-xs px-3 flex items-center gap-1.5"
            data-testid="needs-review-modal-save"
          >
            <Check size={12} />
            {saving ? "Saving…" : "Save override"}
          </button>
        </div>
      </div>
    </div>
  );
}


function ConfidenceDot({ level }) {
  // Iter 39 — per-field confidence indicator. Internal vocabulary
  // ('high'|'medium'|'low'|'none'); user sees a colored dot + 1-word tag.
  // Never surfaces raw scores.
  if (!level) return null;
  const TONE = {
    high:   { color: "#10b981", label: "OK" },
    medium: { color: "#f59e0b", label: "Review" },
    low:    { color: "#ef4444", label: "Low" },
    none:   { color: "#6b7d8a", label: "—" },
  };
  const t = TONE[level] || TONE.none;
  return (
    <span
      className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider"
      style={{ color: t.color }}
      data-testid={`field-confidence-${level}`}
      data-confidence={level}
      title={`Per-field confidence: ${level}`}
    >
      <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: t.color }} />
      {t.label}
    </span>
  );
}


function Field({ label, value, onChange, placeholder, testid, mono, fieldConfidence, ocrOriginal }) {
  return (
    <label className="block">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[9px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
          {label}
        </div>
        <ConfidenceDot level={fieldConfidence} />
      </div>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className={`w-full px-2.5 py-1.5 rounded bg-[var(--fop-surface-2)] border border-[var(--fop-border)] focus:border-[var(--fop-primary)] outline-none text-xs ${mono ? "font-mono" : ""} text-[var(--fop-text)]`}
      />
      {ocrOriginal && ocrOriginal !== value && (
        <div className="text-[9px] font-mono text-[var(--fop-text-4)] mt-0.5 truncate" title={`OCR read: ${ocrOriginal}`}>
          OCR: <span className="text-[var(--fop-text-3)]">{ocrOriginal}</span>
        </div>
      )}
    </label>
  );
}


function DisciplineField({ value, onChange, testid, fieldConfidence }) {
  return (
    <label className="block">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[9px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
          Discipline
        </div>
        <ConfidenceDot level={fieldConfidence} />
      </div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full px-2.5 py-1.5 rounded bg-[var(--fop-surface-2)] border border-[var(--fop-border)] focus:border-[var(--fop-primary)] outline-none text-xs text-[var(--fop-text)]"
      >
        {Object.entries(DISCIPLINE_LABEL).map(([key, label]) => (
          <option key={key} value={key}>{label}</option>
        ))}
      </select>
    </label>
  );
}
